import React, { FormEvent, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowUpRight, ChevronRight, RefreshCw, WifiOff } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { articlesErrorTitle } from '../../api/articlesApi';
import AuthBootScreen from '../../components/auth/AuthBootScreen';
import { useArticles } from '../../hooks/useArticles';
import ThemeToggle from '../../theme/ThemeToggle';
import './articles.css';

const PLAY_STORE_URL =
  'https://play.google.com/store/apps/details?id=com.mocker.mockloc';

const PlayIcon = ({ size = 20 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 512 512" fill="currentColor" aria-hidden="true">
    <path d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6l-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l280.8-161.2-60.1-60.1L104.6 499z" />
  </svg>
);

function formatArticleNumber(number: string): string {
  const digits = number.replace(/\D/g, '');
  if (!digits) {
    return number;
  }
  return digits.padStart(2, '0');
}

interface ArticlesPageProps {
  embedded?: boolean;
}

const ArticlesPage: React.FC<ArticlesPageProps> = ({ embedded = false }) => {
  const { status } = useAuth();
  const { state, reload } = useArticles();
  const [searchText, setSearchText] = useState('');
  const [activeQuery, setActiveQuery] = useState('');
  const isLoggedIn = status === 'authenticated';

  const filteredArticles = useMemo(() => {
    const query = activeQuery.trim().toLowerCase();
    const items = state.status === 'success' ? state.data : [];
    if (!query) {
      return items;
    }

    return items.filter((article) => {
      const searchableText = [article.heading, article.tag, article.number]
        .join(' ')
        .toLowerCase();
      return searchableText.includes(query);
    });
  }, [activeQuery, state]);

  const handleSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setActiveQuery(searchText);
  };

  const clearSearch = () => {
    setSearchText('');
    setActiveQuery('');
  };

  return (
    <div className={`articles-root${embedded ? ' articles-root--embedded' : ''}`}>
      {!embedded && (
      <div className="articles-bg" aria-hidden="true">
        <div className="articles-bg__map" />
        <div className="articles-bg__orb articles-bg__orb--one" />
        <div className="articles-bg__orb articles-bg__orb--two" />
      </div>
      )}

      {!embedded && (
      <nav className="articles-nav" role="navigation">
        <div className="articles-nav__inner">
          <Link to="/" className="articles-nav__brand" aria-label="Mock Loc home">
            <img src="/mockloc-icon.png" alt="Mock Loc app icon" className="articles-nav__logo" />
            <span className="articles-nav__name">Mock Loc</span>
          </Link>
          <div className="articles-nav__actions">
            <ThemeToggle />
            {isLoggedIn ? (
              <>
                <Link to="/articles" className="articles-btn articles-btn--sm articles-btn--outline">
                  Articles
                </Link>
                <Link to="/app" className="articles-btn articles-btn--sm articles-btn--outline">
                  My account
                </Link>
              </>
            ) : (
              <>
                <Link to="/articles" className="articles-btn articles-btn--sm articles-btn--outline">
                  Articles
                </Link>
                <Link to="/login" className="articles-btn articles-btn--sm articles-btn--outline">
                  Login
                </Link>
              </>
            )}
            <a
              href={PLAY_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="articles-btn articles-btn--sm articles-btn--primary"
            >
              <PlayIcon size={15} />
              Play Store
            </a>
          </div>
        </div>
      </nav>
      )}

      <main className="articles-main">
        <section className="articles-hero">
          {!embedded && (
            <>
              <span className="articles-kicker">Mock Loc articles</span>
              <h1>Guides for setting up and using mock location</h1>
              <p>
                Browse setup guides, feature walkthroughs, and tips from the same library used in the
                Mock Loc app. Articles open in a new tab on our knowledge base.
              </p>
            </>
          )}

          <form className="articles-search" onSubmit={handleSearch} role="search">
            <label htmlFor="article-search">Search articles</label>
            <div className="articles-search__row">
              <input
                id="article-search"
                type="search"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                placeholder="Search setup, long press, developer options..."
                disabled={state.status === 'loading'}
              />
              <button type="submit" disabled={state.status === 'loading'}>
                Search
              </button>
            </div>
            {activeQuery && (
              <button type="button" className="articles-search__clear" onClick={clearSearch}>
                Clear search for "{activeQuery}"
              </button>
            )}
          </form>
        </section>

        <section className="articles-list" aria-label="Article list">
          {state.status === 'loading' && (
            <div className="articles-stack" aria-busy="true" aria-label="Loading articles">
              {[0, 1, 2, 3].map((index) => (
                <div className="article-row article-row--skeleton" key={index} aria-hidden="true">
                  <span className="article-row__number article-row__shimmer" />
                  <div className="article-row__content">
                    <span className="article-row__shimmer article-row__shimmer--title" />
                    <span className="article-row__shimmer article-row__shimmer--tag" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {state.status === 'error' && (
            <div className="articles-error" role="alert">
              <div className="articles-error__icon" aria-hidden="true">
                <WifiOff size={28} strokeWidth={2.2} />
              </div>
              <span>{articlesErrorTitle(state.error.kind)}</span>
              <p>{state.error.message}</p>
              <button type="button" onClick={() => void reload()}>
                <RefreshCw size={16} aria-hidden="true" />
                Try again
              </button>
            </div>
          )}

          {state.status === 'success' && (
            <>
              <div className="articles-list__summary">
                <span>
                  {filteredArticles.length}{' '}
                  {filteredArticles.length === 1 ? 'article' : 'articles'}
                </span>
                {activeQuery && <strong>Showing results for "{activeQuery}"</strong>}
              </div>

              {filteredArticles.length > 0 ? (
                <div className="articles-stack">
                  {filteredArticles.map((article) => (
                    <a
                      href={article.articleUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`article-row article-row--${article.accent}`}
                      key={article.id}
                    >
                      <span className="article-row__number">
                        {formatArticleNumber(article.number)}
                      </span>
                      <div className="article-row__content">
                        <h2 className="article-row__title">{article.heading}</h2>
                        {article.tag && (
                          <p className="article-row__desc">{article.tag}</p>
                        )}
                        <span className="article-row__meta">
                          Opens in new tab
                          <ArrowUpRight size={14} aria-hidden="true" />
                        </span>
                      </div>
                      {article.image && (
                        <div className="article-row__thumb">
                          <img src={article.image} alt="" loading="lazy" />
                        </div>
                      )}
                      <span className="article-row__chevron" aria-hidden="true">
                        <ChevronRight size={20} strokeWidth={2.2} />
                      </span>
                    </a>
                  ))}
                </div>
              ) : (
                <div className="articles-empty">
                  <span>No articles found</span>
                  <p>
                    {activeQuery
                      ? `Nothing matched "${activeQuery}". Try a different keyword.`
                      : 'No articles are published yet. Check back soon.'}
                  </p>
                  {activeQuery ? (
                    <button type="button" onClick={clearSearch}>
                      Show all articles
                    </button>
                  ) : (
                    <button type="button" onClick={() => void reload()}>
                      Refresh list
                    </button>
                  )}
                </div>
              )}
            </>
          )}
        </section>
      </main>
    </div>
  );
};

export const PublicArticles: React.FC = () => {
  const { status } = useAuth();

  if (status === 'loading') {
    return <AuthBootScreen />;
  }

  if (status === 'authenticated') {
    return <Navigate to="/app/articles" replace />;
  }

  return <ArticlesPage />;
};

export default ArticlesPage;
